import React, { useState, useEffect } from "react";
import {
  Button,
  ButtonSet,
  DatePicker,
  DatePickerInput,
  Form,
  TextInput,
  Select,
  SelectItem,
} from "carbon-components-react";
import { Formik } from "formik";
import { LoanApplicationBorrower } from "@eave/shared/types/api";
import { BorrowerDelta, updateLoanApp, updateLoanAppBorrower } from "./shared";
import { formatPhoneNumber, stripPhoneNumberSymbols, thereWasAProblem } from "@eave/shared/utils";
import { initialValuesFromBorrower, validationSchema } from "./form-values";
import { maritalStatusOptions } from "web_modules/forms/data/maritalStatusOptions";
import {
  makeDatePickerEvent,
  makeEvent,
  moneyInputToNumber,
} from "web_modules/shared/FormHelpers/carbon-helpers";
import { formatCurrency } from "@eave/shared/utils/currency";
import { loadApp } from "underwriter_dashboard/actions";
import { connect } from "react-redux";

const styles = require("./styles.module.scss");

interface Props {
  appID: number;
  borrower: LoanApplicationBorrower;
  reloadApp: (appID: number) => void;
}

const BorrowerEditor = ({ appID, borrower, reloadApp }: Props) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const updateBorrowers = async (values: BorrowerDelta) => {
    return updateLoanAppBorrower(appID, borrower.borrower_entity, borrower, values);
  };

  const onSubmit = async (values: BorrowerDelta) => {
    setSaving(true);
    try {
      await updateBorrowers(values);
      await updateLoanApp(appID, borrower.borrower_entity, values);
      reloadApp(appID);
    } catch (e) {
      thereWasAProblem("saving dependents changes");
      throw e;
    }
  };

  useEffect(() => {
    if (saving) {
      setSaving(false);
    }
  }, [saving]);

  return (
    <div className={styles.borrowerEditor}>
      <Formik
        initialValues={initialValuesFromBorrower(borrower)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({
          dirty,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          resetForm,
          setValues,
          values,
        }) => {
          const hasErrors = Object.keys(errors).length > 0;

          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
              className={styles.form}
            >
              <div className={styles.buttons}>
                {!editing ? (
                  <Button kind="secondary" size="sm" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                ) : (
                  <ButtonSet>
                    <Button
                      kind="tertiary"
                      size="sm"
                      onClick={() => {
                        setEditing(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button disabled={!dirty || hasErrors} kind="primary" size="sm" type="submit">
                      Save
                    </Button>
                  </ButtonSet>
                )}
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="first_name"
                  name="first_name"
                  value={values.first_name || ""}
                  labelText="First Name"
                  invalid={!!errors.first_name}
                  invalidText={errors.first_name}
                  disabled={!editing || saving}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="last_name"
                  name="last_name"
                  value={values.last_name || ""}
                  labelText="Last Name"
                  invalid={!!errors.last_name}
                  invalidText={errors.last_name}
                  disabled={!editing || saving}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="middle_name"
                  name="middle_name"
                  value={values.middle_name || ""}
                  labelText="Middle Name"
                  invalid={!!errors.middle_name}
                  invalidText={errors.middle_name}
                  disabled={!editing || saving}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
              <div className={styles.inputs}>
                <Select
                  id="suffix"
                  name="suffix"
                  value={values.suffix || ""}
                  invalid={!!errors.suffix}
                  invalidText={errors.suffix}
                  disabled={!editing || saving}
                  labelText="Suffix"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className={styles.fieldHack}
                  size="sm"
                >
                  {/* inline={ true } */}
                  <SelectItem text="--" value="" />
                  <SelectItem text="Sr." value="Sr." />
                  <SelectItem text="Jr." value="Jr." />
                  <SelectItem text="II" value="II" />
                  <SelectItem text="III" value="III" />
                  <SelectItem text="IV" value="IV" />
                  <SelectItem text="V" value="V" />
                  <SelectItem text="VI" value="VI" />
                </Select>
              </div>
              <div className={styles.inputs}>
                <Select
                  id="relationship_status"
                  name="relationship_status"
                  value={values.relationship_status || ""}
                  invalid={!!errors.relationship_status}
                  invalidText={errors.relationship_status}
                  disabled={!editing || saving}
                  labelText="Relationship Status"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className={styles.fieldHack}
                  size="sm"
                >
                  {/* inline={ true } */}
                  <SelectItem text="--" value="" />
                  {maritalStatusOptions.map(option => (
                    <SelectItem
                      key={`maritalStatusOption-${option.value}`}
                      text={option.label}
                      value={option.value}
                    />
                  ))}
                </Select>
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="email"
                  name="email"
                  value={values.email || ""}
                  labelText="Email"
                  invalid={!!errors.email}
                  invalidText={errors.email}
                  disabled={!editing || saving}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
              <div className={styles.inputs}>
                <DatePicker
                  id="dob"
                  name="dob"
                  value={values.dob}
                  disabled={!editing || saving}
                  onChange={event => handleChange(makeDatePickerEvent("dob", event))}
                  onBlur={handleBlur}
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  className={styles.fieldHack}
                >
                  <DatePickerInput
                    id="dob-input"
                    name="dob"
                    disabled={!editing || saving}
                    labelText="Dob"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="phone"
                  name="phone"
                  value={values.phone && formatPhoneNumber(values.phone)}
                  labelText="Phone"
                  invalid={!!errors.phone}
                  invalidText={errors.phone}
                  disabled={!editing || saving}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const e = makeEvent("phone", stripPhoneNumberSymbols(event.target.value));
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
              <div className={styles.inputs}>
                <TextInput
                  id="estimated_monthly_income"
                  name="estimated_monthly_income"
                  value={
                    values.estimated_monthly_income &&
                    formatCurrency(values.estimated_monthly_income, 0, 2)
                  }
                  labelText="Estimated Monthly Income"
                  invalid={!!errors.estimated_monthly_income}
                  invalidText={errors.estimated_monthly_income}
                  disabled={!editing || saving}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(moneyInputToNumber(e));
                  }}
                  onBlur={handleBlur}
                  size="sm"
                  inline={true}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  reloadApp: (appId: number) => loadApp(dispatch, appId),
});

export default connect(null, mapDispatchToProps)(BorrowerEditor);
